import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Employers', path: '/employers' },
    { name: 'Talent', path: '/talent' },
    { name: 'Candidates', path: '/candidates' },
    { name: 'About', path: '/about' },
    { name: 'Events', path: '/events' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white shadow-md py-1'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo with hover animation and shrink on scroll - positioned to align with page content */}
          <Link to="/" className="flex items-center group -ml-2 md:-ml-4 flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-redesign-1/artifacts/7zavuzbi_AimHire%20Logo.png"
              alt="AimHire Logo"
              className={`w-auto transition-all duration-500 ease-out group-hover:scale-105 ${
                isScrolled 
                  ? 'h-28 md:h-44 lg:h-48 brightness-0' 
                  : 'h-36 md:h-56 lg:h-64 brightness-0 invert'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`nav-link text-base xl:text-lg font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  isScrolled
                    ? 'text-slate-600 hover:text-purple-500'
                    : 'text-slate-300 hover:text-purple-200'
                } ${isActive(link.path) ? (isScrolled ? 'text-slate-900 hover:text-purple-500' : 'text-white hover:text-purple-200') : ''}`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/donate" data-testid="donate-button">
              <Button
                className={`rounded-full px-6 xl:px-8 py-2.5 text-base xl:text-lg flex items-center gap-2 transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  isScrolled
                    ? 'bg-slate-900 hover:bg-purple-500 text-white'
                    : 'bg-white/10 hover:bg-purple-200/30 text-white border border-white/30 hover:text-purple-200 hover:border-purple-200'
                }`}
              >
                Donate
                <Heart className="w-4 h-4 xl:w-5 xl:h-5" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={`w-8 h-8 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-8 h-8 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 animate-fade-in"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                className={`block py-3 text-xl font-medium ${
                  isActive(link.path)
                    ? 'text-slate-900'
                    : 'text-slate-600 hover:text-purple-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/donate" className="block mt-4" data-testid="mobile-donate-button">
              <Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-lg py-3 flex items-center justify-center gap-2">
                Donate
                <Heart className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
